import { SmrtCollection } from '@happyvertical/smrt-core';
import { TenantUsageMetric } from '../models/TenantUsageMetric.js';
import type {
  AiUsageSummary,
  RecordUsageOptions,
  SummarizeAiUsageOptions,
  SummarizeUsageBatchOptions,
  SummarizeUsageOptions,
  UsageSummary,
} from '../types.js';
import {
  deterministicUuid,
  normalizeSubscriber,
  stringifyJson,
  subscriberToColumns,
} from '../utils.js';

export class TenantUsageMetricCollection extends SmrtCollection<TenantUsageMetric> {
  static readonly _itemClass = TenantUsageMetric;

  async recordUsage(options: RecordUsageOptions): Promise<TenantUsageMetric> {
    const subscriber = normalizeSubscriber({
      tenantId: options.tenantId,
      subscriberKind: options.subscriberKind,
      subscriberExternalId: options.subscriberExternalId,
    });
    const columns = subscriberToColumns(subscriber);
    const sourcedId =
      options.source && options.sourceId
        ? await deterministicUuid([
            'tenant-usage',
            columns.tenantId,
            columns.subscriberKind,
            columns.subscriberExternalId,
            options.metricKey,
            options.source,
            options.sourceId,
          ])
        : null;
    if (options.source && options.sourceId) {
      const existing = await this.get(sourcedId as string);
      if (existing) return existing;
    }
    try {
      return await this.create({
        ...(sourcedId ? { id: sourcedId } : {}),
        tenantId: columns.tenantId,
        subscriberKind: columns.subscriberKind,
        subscriberExternalId: columns.subscriberExternalId,
        metricKey: options.metricKey,
        quantity: options.quantity,
        windowStart: options.windowStart,
        windowEnd: options.windowEnd,
        source: options.source ?? '',
        sourceId: options.sourceId ?? '',
        projectId: options.projectId ?? '',
        workRefType: options.workRefType ?? '',
        workRefId: options.workRefId ?? '',
        provider: options.provider ?? '',
        dimensions: stringifyJson(options.dimensions ?? {}),
        _insertOnly: Boolean(sourcedId),
      });
    } catch (error) {
      if (sourcedId) {
        const existing = await this.get(sourcedId);
        if (existing) return existing;
      }
      throw error;
    }
  }

  /**
   * Legacy accessor — finds metrics for `subscriberKind = 'tenant'` under the
   * given tenant. External-kind rows scoped to the same tenant are excluded.
   */
  async findByTenantAndMetric(
    tenantId: string,
    metricKey: string,
  ): Promise<TenantUsageMetric[]> {
    return this.list({
      where: { tenantId, metricKey, subscriberKind: 'tenant' },
      orderBy: 'windowStart DESC',
    });
  }

  async summarizeUsage(options: SummarizeUsageOptions): Promise<UsageSummary> {
    const subscriber = normalizeSubscriber({
      tenantId: options.tenantId,
      subscriberKind: options.subscriberKind,
      subscriberExternalId: options.subscriberExternalId,
    });
    const columns = subscriberToColumns(subscriber);

    const where: Record<string, unknown> = {
      tenantId: columns.tenantId,
      subscriberKind: columns.subscriberKind,
      metricKey: options.metricKey,
      'windowStart <': options.window.end.toISOString(),
      'windowEnd >': options.window.start.toISOString(),
    };
    if (subscriber.kind === 'external') {
      where.subscriberExternalId = columns.subscriberExternalId;
    }

    const metrics = await this.list({ where });

    const baseSummary: UsageSummary = {
      tenantId: columns.tenantId,
      metricKey: options.metricKey,
      quantity: metrics.reduce((sum, metric) => sum + metric.quantity, 0),
      windowStart: options.window.start,
      windowEnd: options.window.end,
    };
    if (subscriber.kind === 'external') {
      return {
        ...baseSummary,
        subscriberKind: 'external',
        subscriberExternalId: subscriber.externalId,
      };
    }
    return baseSummary;
  }

  async summarizeUsageBatch(
    options: SummarizeUsageBatchOptions,
  ): Promise<UsageSummary[]> {
    const metricKeys = Array.from(new Set(options.metricKeys));
    if (metricKeys.length === 0) {
      return [];
    }

    const subscriber = normalizeSubscriber({
      tenantId: options.tenantId,
      subscriberKind: options.subscriberKind,
      subscriberExternalId: options.subscriberExternalId,
    });
    const columns = subscriberToColumns(subscriber);

    const metricPlaceholders = metricKeys.map(() => '?').join(', ');
    const params: unknown[] = [
      columns.tenantId,
      columns.subscriberKind,
      ...metricKeys,
      options.window.end.toISOString(),
      options.window.start.toISOString(),
    ];
    let subscriberSql = '';
    if (subscriber.kind === 'external') {
      subscriberSql = 'AND subscriber_external_id = ?';
      params.splice(2, 0, columns.subscriberExternalId);
    }

    const result = await this.db.query(
      `SELECT
          metric_key,
          COALESCE(SUM(quantity), 0) AS quantity
        FROM _smrt_tenant_usage_metrics
        WHERE tenant_id = ?
          AND subscriber_kind = ?
          ${subscriberSql}
          AND metric_key IN (${metricPlaceholders})
          AND window_start < ?
          AND window_end > ?
        GROUP BY metric_key`,
      ...params,
    );

    const quantityByMetric = new Map<string, number>();
    for (const row of resultRows(result)) {
      const metricKey = String(row.metric_key ?? row.metricKey ?? '');
      if (metricKey) {
        quantityByMetric.set(metricKey, numberFromRow(row, 'quantity'));
      }
    }

    return metricKeys.map((metricKey) => {
      const baseSummary: UsageSummary = {
        tenantId: columns.tenantId,
        metricKey,
        quantity: quantityByMetric.get(metricKey) ?? 0,
        windowStart: options.window.start,
        windowEnd: options.window.end,
      };
      if (subscriber.kind === 'external') {
        return {
          ...baseSummary,
          subscriberKind: 'external',
          subscriberExternalId: subscriber.externalId,
        };
      }
      return baseSummary;
    });
  }

  /**
   * Summarize persisted AI usage for a tenant within a window.
   *
   * Reads the framework `_smrt_ai_usage` system table via a raw aggregate
   * query. This deliberately uses `this.db.query` rather than the inherited
   * `query()`: those rows are framework usage records, not `TenantUsageMetric`
   * instances, so model hydration would discard the aggregate columns. Tenant
   * scoping is applied explicitly through the `tenant_id` filter.
   *
   * Named distinctly from the inherited `SmrtClass.summarizeAiUsage` (which
   * returns grouped stats across all tenants) to avoid overriding it.
   */
  async summarizeTenantAiUsage(
    options: SummarizeAiUsageOptions,
  ): Promise<AiUsageSummary> {
    const result = await this.db.query(
      `SELECT
          COALESCE(SUM(prompt_tokens), 0) AS prompt_tokens,
          COALESCE(SUM(completion_tokens), 0) AS completion_tokens,
          COALESCE(SUM(total_tokens), 0) AS total_tokens,
          COALESCE(SUM(estimated_cost), 0) AS estimated_cost,
          COUNT(*) AS request_count
        FROM _smrt_ai_usage
        WHERE tenant_id = ?
          AND created_at >= ?
          AND created_at < ?`,
      options.tenantId,
      options.window.start.toISOString(),
      options.window.end.toISOString(),
    );
    const row = firstRow(result);

    return {
      tenantId: options.tenantId,
      promptTokens: numberFromRow(row, 'prompt_tokens'),
      completionTokens: numberFromRow(row, 'completion_tokens'),
      totalTokens: numberFromRow(row, 'total_tokens'),
      estimatedCost: numberFromRow(row, 'estimated_cost'),
      requestCount: numberFromRow(row, 'request_count'),
      windowStart: options.window.start,
      windowEnd: options.window.end,
    };
  }
}

function firstRow(result: unknown): Record<string, unknown> {
  const rows = resultRows(result);
  return (rows[0] ?? {}) as Record<string, unknown>;
}

function resultRows(result: unknown): Record<string, unknown>[] {
  return Array.isArray(result)
    ? (result as Record<string, unknown>[])
    : ((result as { rows?: Record<string, unknown>[] })?.rows ?? []);
}

function numberFromRow(row: Record<string, unknown>, key: string): number {
  const value = row[key];
  if (typeof value === 'number') {
    return value;
  }
  if (typeof value === 'bigint') {
    return Number(value);
  }
  if (typeof value === 'string') {
    return Number.parseFloat(value) || 0;
  }
  return 0;
}
